import { createContext, useContext, forwardRef } from 'react';
import { cn } from '@repo/utils';

interface FormFieldContextValue {
  htmlFor?: string;
  hasError?: boolean;
  required?: boolean;
}

const FormFieldContext = createContext<FormFieldContextValue>({});

export function useFormField() {
  return useContext(FormFieldContext);
}

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
  htmlFor?: string;
}

export function FormField({
  label,
  required,
  error,
  hint,
  children,
  className,
  htmlFor,
}: FormFieldProps) {
  return (
    <FormFieldContext.Provider value={{ htmlFor, hasError: !!error, required }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        <label
          htmlFor={htmlFor}
          className="text-xs font-medium text-text-secondary tracking-wide"
        >
          {label}
          {required && (
            <span className="text-accent-500 ml-0.5" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {children}
        <p
          id={htmlFor ? `${htmlFor}-error` : undefined}
          className={cn(
            'min-h-[1rem] text-xs',
            error ? 'text-danger-text' : 'text-text-muted',
          )}
          role={error ? 'alert' : undefined}
          aria-live="polite"
        >
          {error ?? hint ?? ''}
        </p>
      </div>
    </FormFieldContext.Provider>
  );
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  startIcon?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, className, startIcon, endAdornment, ...props }, ref) => {
    const ctx = useFormField();
    const describedBy = ctx.htmlFor ? `${ctx.htmlFor}-error` : undefined;

    const sharedProps = {
      'aria-invalid': error ?? undefined,
      'aria-describedby': describedBy || undefined,
      'aria-required': ctx.required || undefined,
      ...props,
    };

    if (startIcon || endAdornment) {
      return (
        <div className="relative flex items-center w-full">
          {startIcon && (
            <span className="absolute left-3 flex items-center pointer-events-none text-text-muted">
              {startIcon}
            </span>
          )}
          <input
            ref={ref}
            {...sharedProps}
            className={cn(
              'w-full h-9 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary placeholder:text-text-muted shadow-[inset_0_1px_2px_rgba(14,20,18,0.06)]',
              'focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 focus:shadow-[inset_0_1px_2px_rgba(14,20,18,0.04)]',
              'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
              startIcon ? 'pl-9' : 'pl-3',
              endAdornment ? 'pr-9' : 'pr-3',
              error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500',
              className,
            )}
          />
          {endAdornment && (
            <span className="absolute right-2.5 flex items-center">
              {endAdornment}
            </span>
          )}
        </div>
      );
    }

    return (
      <input
        ref={ref}
        {...sharedProps}
        className={cn(
          'w-full h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary placeholder:text-text-muted shadow-[inset_0_1px_2px_rgba(14,20,18,0.06)]',
          'focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 focus:shadow-[inset_0_1px_2px_rgba(14,20,18,0.04)]',
          'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
          error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500',
          className,
        )}
      />
    );
  }
);

Input.displayName = 'Input';
